export interface EventAnalysisAlternative {
  label: string;
  calibratedProbability?: number;
}

export interface EventAnalysisWaveformMeasurementGroup {
  label: string;
  measurements: string[];
}

export interface EventAnalysisPresentation {
  recorderTrigger?: string;
  waveformSignature?: string;
  estimatedCause: string;
  headerSummary?: string;
  calibratedProbability?: number;
  evidenceLevel?: 'High' | 'Moderate' | 'Low' | 'Insufficient';
  reason?: string;
  phenomena: string[];
  alternatives: EventAnalysisAlternative[];
  evidence: string[];
  limitations: string[];
  noveltyStatus?: string;
  contextSummary?: string;
  versions: string[];
  waveformMeasurementGroups: EventAnalysisWaveformMeasurementGroup[];
}

export interface EventClassification {
  key: string;
  label: string;
}

interface EventAnalysisResponse {
  analysis?: unknown;
  analysis_status?: unknown;
}

type UnknownRecord = Record<string, unknown>;

const UNAVAILABLE_STATUSES = new Set([
  'unavailable',
  'not_available',
  'not_requested',
  'not_found',
  'missing',
  'disabled',
  'error'
]);

const PRESENTABLE_STATUSES = new Set([
  'available',
  'classified',
  'abstained',
  'insufficient',
  'insufficient_evidence'
]);

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function cleanText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const text = value.replace(/\s+/g, ' ').trim();
  return text || undefined;
}

function firstText(record: UnknownRecord, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = cleanText(record[key]);
    if (value) return value;
  }
  return undefined;
}

function normalizedStatus(value: unknown): string {
  return cleanText(value)?.toLowerCase().replace(/[\s-]+/g, '_') ?? '';
}

function humanizeLabel(value: string): string {
  const text = value.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return text;
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

const CAUSE_LABELS: Readonly<Record<string, string>> = {
  fault_like_disturbance: 'Fault-like disturbance',
  fault_induced_voltage_dip: 'Fault-induced voltage dip',
  interruption: 'Interruption',
  neutral_overcurrent: 'Neutral over-current',
  no_event: 'No event',
  over_frequency: 'Over Frequency',
  phase_overcurrent: 'Phase over-current',
  restriking_arcing_fault: 'Incipient fault',
  single_phase_fault: 'Single-phase fault',
  sustained_arcing_fault: 'Incipient fault',
  transient_phase_to_phase_short_circuit: 'Transient phase-to-phase fault',
  under_frequency: 'Under frequency',
  upstream_voltage_dip: 'Upstream voltage dip',
  voltage_dip: 'Voltage dip',
  voltage_rise: 'Voltage rise',
  waveshape_change: 'Waveshape change'
};

const CAUSE_HEADER_SUMMARIES: Readonly<Record<string, string>> = {
  fault_induced_voltage_dip: 'Short two-phase unbalanced voltage dip with load recovery',
  upstream_voltage_dip: 'Fault indication: upstream-network or parallel-feeder fault'
};

function presentationText(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const text = value
    .replace(/\bincipient[- ]fault candidate\b/gi, 'incipient fault')
    .replace(/\balternating arc-current cores?\b/gi, 'arcing half-cycles')
    .replace(/\bload-(increase|decrease) candidate\b/gi, 'load $1')
    .replace(/\bmotor[- ]energisation candidate\b/gi, 'motor energisation')
    .replace(/\bswitching[- ]transient candidate\b/gi, 'switching transient')
    .replace(/\bcandidate\b/gi, 'classification')
    .replace(/\bmost likely\b/gi, 'favoured')
    .replace(/\blikely\b\s*/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  return text || undefined;
}

function rawLabelFrom(value: unknown): string | undefined {
  const direct = cleanText(value);
  if (direct) return direct;
  if (!isRecord(value)) return undefined;
  return firstText(value, ['label', 'name', 'cause', 'type', 'category', 'description']);
}

function labelFrom(value: unknown): string | undefined {
  const label = rawLabelFrom(value);
  return label ? humanizeLabel(label) : undefined;
}

function causeKeyFrom(value: unknown): string | undefined {
  const label = rawLabelFrom(value);
  if (!label) return undefined;
  const key = normalizedStatus(label);
  return ['restriking_arcing_fault', 'sustained_arcing_fault'].includes(key)
    ? 'incipient_fault_candidate'
    : key;
}

function causeLabelFrom(value: unknown): string | undefined {
  const rawLabel = rawLabelFrom(value);
  if (!rawLabel) return undefined;
  return CAUSE_LABELS[normalizedStatus(rawLabel)]
    ?? humanizeLabel(rawLabel).replace(/\s+candidate$/i, '');
}

function probabilityFrom(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 1) return undefined;
  return value * 100;
}

function calibratedProbability(value: unknown): number | undefined {
  if (!isRecord(value)) return undefined;
  if (value.probability_calibrated !== true) return undefined;
  return probabilityFrom(value.probability ?? value.confidence);
}

function unique(values: Array<string | undefined>): string[] {
  const result: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    if (!value) continue;
    const key = value.toLocaleLowerCase('en-GB');
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(value);
  }
  return result;
}

function labelsFrom(value: unknown): string[] {
  const values = Array.isArray(value) ? value : value === undefined || value === null ? [] : [value];
  return unique(values.map(labelFrom));
}

function phenomenaFrom(value: unknown): { signature?: string; details: string[] } {
  const values = Array.isArray(value) ? value : value === undefined || value === null ? [] : [value];
  const summaries: string[] = [];
  const details: string[] = [];
  for (const item of values) {
    const label = presentationText(labelFrom(item));
    if (!label) continue;
    const phases = isRecord(item) ? labelsFrom(item.phases) : [];
    const summary = phases.length ? `${label} (${phases.join(', ')})` : label;
    const detail = isRecord(item)
      ? presentationText(firstText(item, ['detail', 'description', 'reason']))
      : undefined;
    summaries.push(summary);
    details.push(detail ? `${summary} — ${detail}` : summary);
  }
  const uniqueSummaries = unique(summaries);
  return {
    signature: uniqueSummaries.length ? uniqueSummaries.join('; ') : undefined,
    details: unique(details)
  };
}

function formatValue(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value.toLocaleString('en-GB', { maximumFractionDigits: 3 });
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return cleanText(value);
}

function presentationUnit(value: unknown): string | undefined {
  const unit = cleanText(value);
  if (!unit) return undefined;
  const normalized = unit.toLowerCase().replace(/[\s-]+/g, '_');
  if (normalized === 'boolean') return undefined;
  if (normalized === 'ratio') return '\u00d7';
  if (['per_unit', 'pu', 'p.u.', 'p.u'].includes(normalized)) return 'p.u.';
  return unit;
}

function formatMeasurement(value: unknown): string | undefined {
  const direct = cleanText(value);
  if (direct) return direct;
  if (!isRecord(value)) return undefined;

  const description = firstText(value, ['description', 'text', 'reason', 'explanation']);
  if (description) return description;

  const label = firstText(value, ['label', 'name', 'metric', 'feature']);
  const hasMeasurement = ['display_value', 'value', 'observed'].some((key) => key in value);
  const measured = formatValue(value.display_value ?? value.value ?? value.observed);
  if (!label && !measured) return undefined;
  if (hasMeasurement && !measured) return undefined;
  if (!measured) return label ? humanizeLabel(label) : undefined;

  const unit = presentationUnit(value.unit);
  const separator = unit && ['%', '\u00b0', '\u00d7'].includes(unit) ? '' : unit ? ' ' : '';
  const formatted = `${measured}${separator}${unit ?? ''}`;
  const phase = firstText(value, ['phase']);
  const measurementLabel = label
    ? `${humanizeLabel(label)}${phase ? ` (${phase})` : ''}`
    : undefined;
  return measurementLabel ? `${measurementLabel}: ${formatted}` : formatted;
}

function measurementsFrom(value: unknown): string[] {
  const values = Array.isArray(value) ? value : value === undefined || value === null ? [] : [value];
  return unique(values.map(formatMeasurement).map(presentationText));
}

function waveformMeasurementGroupsFrom(value: unknown): EventAnalysisWaveformMeasurementGroup[] {
  if (!isRecord(value) || value.measurement_schema_version !== 1 || !Array.isArray(value.groups)) return [];

  const groups: EventAnalysisWaveformMeasurementGroup[] = [];
  for (const candidate of value.groups) {
    if (!isRecord(candidate) || !Array.isArray(candidate.measurements)) continue;
    const label = cleanText(candidate.label);
    if (!label) continue;

    const measurements = unique(candidate.measurements.map((measurement) => {
      if (!isRecord(measurement)) return undefined;
      const metric = cleanText(measurement.metric);
      const unit = cleanText(measurement.unit);
      const measured = measurement.value;
      if (!metric || !unit || typeof measured !== 'number' || !Number.isFinite(measured)) return undefined;

      const hasPhase = Object.prototype.hasOwnProperty.call(measurement, 'phase');
      const phase = cleanText(measurement.phase);
      if (hasPhase && !phase) return undefined;
      return formatMeasurement({ metric, value: measured, unit, ...(phase ? { phase } : {}) });
    }));
    if (measurements.length) groups.push({ label, measurements });
  }
  return groups;
}

function normalizeEvidenceLevel(value: unknown): EventAnalysisPresentation['evidenceLevel'] {
  const level = normalizedStatus(value);
  if (['high', 'strong'].includes(level)) return 'High';
  if (['moderate', 'medium'].includes(level)) return 'Moderate';
  if (['low', 'weak'].includes(level)) return 'Low';
  if (['insufficient', 'none', 'unrated'].includes(level)) return 'Insufficient';
  return undefined;
}

function alternativesFrom(value: unknown): EventAnalysisAlternative[] {
  const values = Array.isArray(value) ? value : value === undefined || value === null ? [] : [value];
  const result: EventAnalysisAlternative[] = [];
  const seen = new Set<string>();
  for (const item of values) {
    const label = causeLabelFrom(item);
    if (!label) continue;
    const key = label.toLocaleLowerCase('en-GB');
    if (seen.has(key)) continue;
    seen.add(key);
    const probability = calibratedProbability(item);
    result.push({ label, ...(probability === undefined ? {} : { calibratedProbability: probability }) });
  }
  return result;
}

function noveltyFrom(value: unknown): string | undefined {
  const direct = cleanText(value);
  if (direct) {
    const status = normalizedStatus(direct);
    return ['not_evaluated', 'unavailable', 'unknown'].includes(status) ? undefined : humanizeLabel(direct);
  }
  if (!isRecord(value)) return undefined;
  const status = firstText(value, ['status', 'label', 'assessment']);
  return status && !['not_evaluated', 'unavailable', 'unknown'].includes(normalizedStatus(status))
    ? humanizeLabel(status)
    : undefined;
}

function integerFrom(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined;
}

function contextSummaryFrom(value: unknown): string | undefined {
  if (!isRecord(value)) return undefined;
  const incident = isRecord(value.incident) ? value.incident : value;
  const similarity = isRecord(value.similarity) ? value.similarity : value;
  const monitorCount = integerFrom(incident.monitor_count);
  const prior = integerFrom(similarity.prior_similar_events_30d) ?? 0;
  const subsequent = integerFrom(similarity.subsequent_similar_events_30d) ?? 0;
  const similar = prior + subsequent;
  const retrospective = normalizedStatus(value.analysis_mode) === 'retrospective';
  const retrospectiveInputs = Array.isArray(value.retrospective_inputs)
    ? value.retrospective_inputs.map(normalizedStatus)
    : [];
  const parts: string[] = [];
  if (retrospectiveInputs.includes('later_recovery_window')) {
    parts.push('a later recovery window was used');
  }
  if (similar > 0) {
    const timing = [
      prior ? `${prior} earlier` : '',
      subsequent ? `${subsequent} later` : ''
    ].filter(Boolean).join(', ');
    parts.push(`${similar} event${similar === 1 ? '' : 's'} with similar extracted waveform features at this monitor within 30 days${timing ? ` (${timing})` : ''}`);
  }
  if (monitorCount !== undefined && monitorCount > 1) {
    parts.push(`time-coincident captures occurred at ${monitorCount} monitors (clock and topology unverified)`);
  }
  if (retrospective) {
    return `Retrospective analysis${parts.length ? `: ${parts.join('; ')}` : ''}`;
  }
  return parts.length ? `Event context: ${parts.join('; ')}` : undefined;
}

function causeForStatus(status: string, cause: string | undefined): string {
  if (status.includes('insufficient')) return 'Insufficient evidence';
  if (['abstained', 'uncertain', 'unknown', 'novel', 'out_of_distribution'].includes(status)) return 'Uncertain';
  if (!cause || ['unknown', 'uncertain', 'abstained'].includes(normalizedStatus(cause))) return 'Uncertain';
  return cause;
}

export function eventClassificationFromAnalysis(analysis: unknown): EventClassification | undefined {
  if (!isRecord(analysis)) return undefined;
  const statedStatus = normalizedStatus(analysis.analysis_status ?? analysis.status);
  const status = analysis.abstained === true ? 'abstained' : statedStatus;
  if (UNAVAILABLE_STATUSES.has(status) || !PRESENTABLE_STATUSES.has(status)) return undefined;
  const causeValue = analysis.estimated_cause ?? analysis.estimated_event_type ?? analysis.cause;
  const causeKey = causeKeyFrom(causeValue);
  const label = causeForStatus(status, causeLabelFrom(causeValue));
  if (label === 'Insufficient evidence') return { key: 'status:insufficient-evidence', label };
  if (label === 'Uncertain') return { key: 'status:uncertain', label };
  const stableCauseKey = (causeKey ?? normalizedStatus(label))
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'unknown';
  return { key: `cause:${stableCauseKey}`, label };
}

export function eventClassificationLabel(analysis: unknown): string | undefined {
  return eventClassificationFromAnalysis(analysis)?.label;
}

/**
 * Converts the deliberately extensible event-analysis wire object into a small,
 * safe UI model. Unknown fields are ignored and unavailable analysis produces no
 * UI, preserving compatibility with servers that do not have the sidecar.
 */
export function eventAnalysisFromResponse(response: EventAnalysisResponse): EventAnalysisPresentation | null {
  const analysis = isRecord(response.analysis)
    ? response.analysis
    : response.analysis === undefined && typeof response.analysis_status === 'string'
      ? { analysis_status: response.analysis_status }
      : null;
  if (!analysis) return null;

  const statedStatus = normalizedStatus(analysis.analysis_status ?? analysis.status);
  const status = analysis.abstained === true ? 'abstained' : statedStatus;
  if (UNAVAILABLE_STATUSES.has(status)) return null;
  if (!PRESENTABLE_STATUSES.has(status)) return null;

  const causeValue = analysis.estimated_cause ?? analysis.estimated_event_type ?? analysis.cause;
  const causeKey = causeKeyFrom(causeValue);
  const cause = causeLabelFrom(causeValue);
  const estimatedCause = causeForStatus(status, cause);
  const headerSummary = estimatedCause === cause
    ? presentationText(
      firstText(analysis, ['header_summary', 'secondary_cause', 'extra_info'])
    ) ?? (causeKey ? CAUSE_HEADER_SUMMARIES[causeKey] : undefined)
    : undefined;
  const globalCalibration = analysis.probability_calibrated === true;
  const causeProbability = globalCalibration
    ? probabilityFrom(analysis.probability)
    : calibratedProbability(causeValue);
  const evidence = measurementsFrom(analysis.evidence ?? analysis.measured_evidence ?? analysis.features);
  const explicitReason = presentationText(firstText(analysis, ['reason', 'explanation', 'summary']));
  const reason = explicitReason ?? evidence[0];
  const recorderTrigger = firstText(analysis, ['recorder_trigger', 'trigger', 'trigger_type']);
  const phenomenonPresentation = phenomenaFrom(analysis.phenomena ?? analysis.observed_phenomena);
  const alternatives = alternativesFrom(analysis.alternatives ?? analysis.alternative_causes)
    .filter((alternative) => alternative.label.toLocaleLowerCase('en-GB') !== estimatedCause.toLocaleLowerCase('en-GB'));
  const limitations = measurementsFrom(analysis.limitations ?? analysis.caveats ?? analysis.warnings);
  const waveformMeasurementGroups = waveformMeasurementGroupsFrom(analysis.waveform_measurements);
  const noveltyStatus = noveltyFrom(analysis.novelty_status ?? analysis.novelty);
  const contextSummary = contextSummaryFrom(analysis.context ?? analysis.event_context);
  const versionRecord = isRecord(analysis.versions) ? analysis.versions : {};
  const modelVersion = firstText(versionRecord, ['algorithm', 'model']) ?? firstText(analysis, ['model_version']);
  const taxonomyVersion = firstText(versionRecord, ['taxonomy']) ?? firstText(analysis, ['taxonomy_version']);
  const featureVersion = formatValue(versionRecord.features)
    ?? firstText(analysis, ['feature_version', 'feature_schema_version']);
  const methodLabel = modelVersion && /^(deterministic|rule)/i.test(modelVersion) ? 'Method' : 'Model';
  const versions = unique([
    modelVersion ? `${methodLabel} ${modelVersion}` : undefined,
    taxonomyVersion ? `Taxonomy ${taxonomyVersion}` : undefined,
    featureVersion ? `Features ${featureVersion}` : undefined
  ]);

  return {
    recorderTrigger,
    waveformSignature: phenomenonPresentation.signature,
    estimatedCause,
    ...(headerSummary ? { headerSummary } : {}),
    ...(causeProbability === undefined ? {} : { calibratedProbability: causeProbability }),
    evidenceLevel: normalizeEvidenceLevel(analysis.evidence_level ?? analysis.confidence_level),
    reason,
    phenomena: phenomenonPresentation.details,
    alternatives,
    evidence,
    limitations,
    noveltyStatus,
    contextSummary,
    versions,
    waveformMeasurementGroups
  };
}

export function formatCalibratedProbability(probability: number): string {
  const rounded = probability >= 10 && probability <= 99
    ? Math.round(probability)
    : Math.round(probability * 10) / 10;
  return `${rounded}% calibrated`;
}
